"use client";

import { useLocale } from "next-intl";
import React, { type ReactElement } from "react";
import {
  FeatureCard,
  FeatureCardContent,
  FeatureCardTitle,
  FeatureCardDescription,
  FeatureCardLink,
  ObservatoryCard,
} from "@/components";
import { Project } from "@/types";
import { motion } from "framer-motion";

interface ProjectsListProps {
  projects: Project[];
}

function ProjectsList({ projects }: Readonly<ProjectsListProps>): ReactElement {
  const locale = useLocale();

  return (
    <section className="max-w-7xl mx-auto p-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
      {projects.map((project) => {
        return (
          <div
            key={project.id}
            className="relative border bg-background rounded-2xl grid grid-cols-1 gap-4 bg-linear-to-b from-transparent to-accent xl:grid-cols-2 xl:gap-0"
          >
            <FeatureCard
              key={project.id}
              className="relative flex flex-col text-center xl:text-left"
            >
              <FeatureCardContent className="relative z-20 flex-1 text-foreground justify-end items-center">
                <FeatureCardTitle>{project.name[locale]}</FeatureCardTitle>
                {project.description ? (
                  <FeatureCardDescription className="text-base leading-tight text-shadow-inherit opacity-70">
                    {project.description[locale]}
                  </FeatureCardDescription>
                ) : null}
                {project.url ? <FeatureCardLink href={project.url} /> : null}
              </FeatureCardContent>
            </FeatureCard>
            {project.observatory ? (
              <motion.div
                className="w-[320px] p-6 mx-auto rotate-6"
                animate={{ y: [200, 0] }}
                transition={{
                  duration: 0.4,
                  ease: "easeInOut",
                }}
              >
                <ObservatoryCard observatory={project.observatory} />
              </motion.div>
            ) : null}
          </div>
        );
      })}
    </section>
  );
}

export { ProjectsList, type ProjectsListProps };
